import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuthStore } from "../lib/auth";
import { useCardsStore } from "../lib/store";
import {
  detectTsubameSheet,
  ensureTagsColumn,
  fetchAllCards,
  filterCards,
  GoogleApiError,
  updateCardTags,
  VISIBLE_COLUMNS,
} from "../lib/sheets";
import { extractAllTags, filterByTags } from "../lib/tags";
import NoSheetGuide from "../components/NoSheetGuide";
import TagBadge from "../components/TagBadge";
import TagEditor from "../components/TagEditor";
import TagFilter from "../components/TagFilter";
import type { BusinessCard } from "../lib/types";

export default function CardsPage() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const signOut = useAuthStore((s) => s.signOut);

  const status = useCardsStore((s) => s.status);
  const sheet = useCardsStore((s) => s.sheet);
  const cards = useCardsStore((s) => s.cards);
  const error = useCardsStore((s) => s.error);
  const setStatus = useCardsStore((s) => s.setStatus);
  const setSheet = useCardsStore((s) => s.setSheet);
  const setCards = useCardsStore((s) => s.setCards);
  const setError = useCardsStore((s) => s.setError);

  const [query, setQuery] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [editingCard, setEditingCard] = useState<BusinessCard | null>(null);

  const loadCards = async () => {
    if (!accessToken) return;
    setStatus("loading");
    setError(null);
    try {
      const detected = await detectTsubameSheet(accessToken);
      if (!detected) {
        setSheet(null);
        setCards([]);
        setStatus("no_sheet");
        return;
      }
      setSheet(detected);
      // 既存シートに W列(タグ)が無ければ自動追加(冪等)
      await ensureTagsColumn(detected.id, accessToken).catch(() => {
        // 失敗してもタグ無し状態で動かす(ユーザー側に Drive 権限がない等のレア)
      });
      const rows = await fetchAllCards(detected.id, accessToken);
      setCards(rows);
      setStatus("ready");
    } catch (err) {
      const msg =
        err instanceof GoogleApiError
          ? `${err.message}(認証が切れていれば再ログインしてください)`
          : err instanceof Error
            ? err.message
            : "予期しないエラーが発生しました";
      setError(msg);
      setStatus("error");
    }
  };

  useEffect(() => {
    if (accessToken && status === "idle") {
      void loadCards();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const allTags = useMemo(() => extractAllTags(cards), [cards]);
  const filtered = useMemo(() => {
    const a = filterCards(cards, query);
    return filterByTags(a, selectedTags);
  }, [cards, query, selectedTags]);

  const toggleTagFilter = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const handleSaveTags = async (tags: string[]) => {
    if (!editingCard || !sheet || !accessToken || !editingCard.rowIndex) return;
    await updateCardTags(sheet.id, editingCard.rowIndex, tags, accessToken);
    // ローカルの cards も更新(再フェッチ不要)
    setCards(
      cards.map((c) =>
        c.rowIndex === editingCard.rowIndex ? { ...c, tags } : c,
      ),
    );
  };

  const formatDate = (raw: string | undefined): string => {
    if (!raw) return "";
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      return d.toLocaleDateString("ja-JP", { year: "numeric", month: "2-digit", day: "2-digit" });
    } catch {
      return raw;
    }
  };

  return (
    <main className="min-h-screen bg-washi">
      <header className="flex items-center justify-between border-b border-washiDeep px-6 py-4">
        <h1 className="serif text-xl font-medium text-navy">つばめカード</h1>
        <nav className="flex gap-2 text-sm">
          <Link to="/manual" className="btn-primary">＋ 追加</Link>
          <Link to="/import/csv" className="btn-secondary">CSV</Link>
          <Link to="/import/vcf" className="btn-secondary">VCF</Link>
          <Link to="/settings" className="btn-secondary">設定</Link>
          <button type="button" onClick={signOut} className="btn-secondary">
            ログアウト
          </button>
        </nav>
      </header>

      {status === "loading" && (
        <div className="px-6 py-16 text-center text-sm text-navySoft">
          シートを検索しています...
        </div>
      )}

      {status === "error" && (
        <div className="mx-auto max-w-2xl px-6 py-16 text-center">
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
          <button type="button" onClick={loadCards} className="btn-primary mt-6">
            再試行
          </button>
        </div>
      )}

      {status === "no_sheet" && <NoSheetGuide onRetry={loadCards} />}

      {status === "ready" && (
        <section className="mx-auto max-w-7xl px-6 py-8">
          <div className="mb-4 flex items-center justify-between gap-4">
            <label htmlFor="cards-search" className="sr-only">
              名刺を検索
            </label>
            <input
              id="cards-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="氏名・会社・メール・電話で検索..."
              className="w-full max-w-md rounded-md border border-washiDeep bg-white px-4 py-2 text-sm shadow-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy/30"
            />
            <div className="flex items-center gap-3 text-xs text-navySoft">
              <span>
                {filtered.length === cards.length
                  ? `全 ${cards.length} 件`
                  : `${filtered.length} 件 / 全 ${cards.length} 件`}
              </span>
              <button
                type="button"
                onClick={loadCards}
                className="rounded border border-washiDeep px-2 py-1 hover:bg-washiDeep"
                title="シートから再読み込み"
              >
                ↻ 更新
              </button>
            </div>
          </div>

          <TagFilter
            allTags={allTags}
            selected={selectedTags}
            onToggle={toggleTagFilter}
            onClear={() => setSelectedTags([])}
          />

          {sheet && (
            <p className="mb-3 text-xs text-inkLight">
              シート:{" "}
              <a
                href={`https://docs.google.com/spreadsheets/d/${sheet.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-navy"
              >
                {sheet.name}
              </a>{" "}
              · 最終更新 {new Date(sheet.modifiedTime).toLocaleString("ja-JP")}
            </p>
          )}

          {cards.length === 0 ? (
            <div className="rounded-lg border border-washiDeep bg-white p-12 text-center text-sm text-navySoft">
              名刺がまだ登録されていません。iOS 版でスキャンするか、
              <Link to="/manual" className="ml-1 underline hover:text-navy">
                手動で追加
              </Link>
              ・
              <Link to="/import/csv" className="ml-0.5 underline hover:text-navy">
                CSV
              </Link>
              ・
              <Link to="/import/vcf" className="ml-0.5 underline hover:text-navy">
                VCF
              </Link>
              から始めましょう。
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-washiDeep bg-white shadow-sm">
              <table className="min-w-full text-sm">
                <thead className="bg-washiDeep/60 text-left">
                  <tr>
                    {VISIBLE_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        className={`whitespace-nowrap px-3 py-2 font-medium text-navy ${col.width}`}
                      >
                        {col.label}
                      </th>
                    ))}
                    <th className="min-w-[18rem] whitespace-nowrap px-3 py-2 font-medium text-navy">タグ</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-washiDeep/50">
                  {filtered.map((card, i) => (
                    <tr key={`${card.no || i}`} className="hover:bg-washi">
                      {VISIBLE_COLUMNS.map((col) => {
                        const raw = card[col.key];
                        const value =
                          typeof raw === "string"
                            ? raw
                            : Array.isArray(raw)
                              ? raw.join(", ")
                              : raw == null
                                ? ""
                                : String(raw);
                        return (
                          <td
                            key={col.key}
                            className={`truncate whitespace-nowrap px-3 py-2 text-navySoft ${col.width}`}
                            title={value}
                          >
                            {col.key === "acquiredAt" ? formatDate(value) : value}
                          </td>
                        );
                      })}
                      <td className="min-w-[18rem] px-3 py-2 align-top">
                        <button
                          type="button"
                          onClick={() => setEditingCard(card)}
                          className="flex w-full flex-wrap items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-washiDeep"
                          title="タグを編集"
                        >
                          {(card.tags ?? []).length === 0 ? (
                            <span className="text-xs text-inkLight">＋ 追加</span>
                          ) : (
                            (card.tags ?? []).map((t) => (
                              <TagBadge key={t} tag={t} size="sm" />
                            ))
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filtered.length === 0 && cards.length > 0 && (
            <div className="mt-6 text-center text-sm text-navySoft">
              一致する名刺が見つかりません
            </div>
          )}
        </section>
      )}

      {editingCard && (
        <TagEditor
          cardName={editingCard.name || "(名前未設定)"}
          initialTags={editingCard.tags ?? []}
          knownTags={allTags.map((t) => t.tag)}
          onSave={handleSaveTags}
          onClose={() => setEditingCard(null)}
        />
      )}
    </main>
  );
}
